/**
 * The canvas's life: its size (following the window and the screen's pixel density), the
 * theme colours (repainted when the theme or the config changes), who paints (the graphics
 * card or the processor, see `renderer.ts`), and the animation loop that runs the layout
 * simulation and repaints when {@link Scene.redraw} is set.
 *
 * **Deferred painting**: when painting got slow (a huge graph, or a processor drawing it) and
 * only the view moves (a pan, a zoom), the last picture is moved on screen with a CSS transform,
 * which costs nothing, and painted again once the view rests for {@link REST_MS}, or every
 * {@link REFRESH_MS} during a long gesture (so what comes into view appears). Panning and
 * zooming then follow the pointer at the screen's rate however big the graph.
 */
import { WORKER_FROM, type Scene } from "../../state/scene";
import { missingColours, readPalette, type Palette } from "./palette";
import { createRenderer, type GraphRenderer, type RendererKind, type RendererLog } from "./renderer";
import type { ImageLookup, Picture } from "./shapes";
import { BIG_COOLING, COOLING, REFRESH_MS, REST_ENERGY, REST_MS, SLOW_PAINT_MS } from "./constants";

/** What the loop calls. */
export interface CanvasHooks {
  /** What to paint in this frame. */
  picture(palette: Palette): Picture;
  /** The loaded pictures the picture's image shapes name (none: they are not drawn). */
  images?: ImageLookup;
  /** Whether to use the graphics card when the device has one, at first. */
  gpu: boolean;
  /** Says which renderer is used, and why the graphics card is not. */
  log: RendererLog;
  /** The layout stopped moving (a good time to share positions). */
  settled(): void;
  /**
   * The colours had been read before the stylesheet applied (`missing` were empty) and are now
   * complete, `ms` milliseconds later. Until then they are read again on every frame.
   */
  coloursLate?(missing: string[], ms: number): void;
}

/** The view (pan and zoom) a picture was painted with. */
type PaintedView = { x: number; y: number; k: number; width: number; height: number };

/** The running canvas. */
export interface CanvasControl {
  /** Stops everything (the loop, the observers, the graphics card). */
  stop(): void;
  /** Switches to the graphics card (when the device has one) or to the processor. */
  setGpu(on: boolean): void;
  /** Who paints right now. */
  renderer(): RendererKind;
}

/**
 * Sizes `canvas` to `host` and starts the loop. The scene's size is the canvas's size in CSS
 * pixels.
 */
export function startCanvas(host: HTMLElement, canvas: HTMLCanvasElement, scene: Scene, hooks: CanvasHooks): CanvasControl {
  let renderer: GraphRenderer = createRenderer(canvas, hooks.gpu, hooks.log);
  let gpu = hooks.gpu;
  let palette = readPalette();
  let dpr = 1;
  let frameId = 0;
  let wasMoving = false;
  /** The colours that read empty at first, and when (for {@link CanvasHooks.coloursLate}). */
  let late: { missing: string[]; since: number } | null = null;

  /** Follows the host's size, keeping what was in the middle there (a window docked beside the graph does not push it aside); the first real size also frames the graph. */
  const resize = () => {
    const rect = host.getBoundingClientRect();
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    const firstSize = scene.width <= 1;
    const [width, height] = [Math.max(1, Math.round(rect.width)), Math.max(1, Math.round(rect.height))];
    if (!firstSize) {
      scene.view.x += (width - scene.width) / 2;
      scene.view.y += (height - scene.height) / 2;
    }
    scene.width = width;
    scene.height = height;
    canvas.width = Math.round(scene.width * dpr);
    canvas.height = Math.round(scene.height * dpr);
    renderer.resize(scene.width, scene.height, dpr);
    if (firstSize && scene.laidOut) {
      if (scene.nodes.length) scene.fit();
      else Object.assign(scene.view, { x: scene.width / 2, y: scene.height / 2 });
    }
    scene.redraw = true;
  };
  const observer = new ResizeObserver(resize);
  observer.observe(host);
  resize();

  /** Re-reads the colours (theme switched, config edited, system went dark). */
  const repaint = () => {
    palette = readPalette();
    scene.redraw = true;
  };
  const themeObserver = new MutationObserver(repaint);
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "data-config-revision"] });
  const media = matchMedia("(prefers-color-scheme: dark)");
  media.addEventListener("change", repaint);

  /** When the last frame ran (for gliding to the shared layout). */
  let lastFrame = performance.now();
  /** The view of the picture on screen, how long painting it took, and when it was painted. */
  let painted: PaintedView | null = null;
  let paintMs = 0;
  let paintedAt = 0;
  /** When the view last moved while a paint was deferred (0: none waiting). */
  let movedAt = 0;
  /** How many view moves were shown by moving the last picture instead (for the browser checks). */
  let deferred = 0;

  /**
   * Whether this frame may move the picture on screen instead of painting: painting is slow, the
   * layout rests, only the pan or zoom changed since the last paint, and it was not too long ago.
   */
  const mayDefer = (now: number): boolean => {
    if (!painted || paintMs < SLOW_PAINT_MS || scene.alpha > 0 || now - paintedAt > REFRESH_MS) return false;
    const v = scene.view;
    if (painted.width !== scene.width || painted.height !== scene.height) return false;
    return v.x !== painted.x || v.y !== painted.y || v.k !== painted.k;
  };

  /** One animation frame: a simulation step while the layout moves, then a paint if needed. */
  const frame = (now: number) => {
    // Nodes gliding to where someone else's layout put them (a long pause jumps to the end).
    scene.stepFollow(Math.min(now - lastFrame, 1000));
    lastFrame = now;
    if (scene.alpha > REST_ENERGY) {
      // A big graph's step may wait for its pushes from the worker: no step, no cooling.
      if (scene.tick()) {
        scene.alpha *= scene.nodes.length >= WORKER_FROM ? BIG_COOLING : COOLING;
        scene.redraw = true;
      }
      wasMoving = true;
    } else {
      scene.alpha = 0;
      if (wasMoving) {
        wasMoving = false;
        scene.releasePlaced();
        hooks.settled();
      }
    }
    // Colours read before the stylesheet applied are empty: read them again until they are not.
    const missing = missingColours(palette);
    if (missing.length) {
      late ??= { missing, since: performance.now() };
      palette = readPalette();
      scene.redraw = true;
      if (!missingColours(palette).length) hooks.coloursLate?.(late.missing, Math.round(performance.now() - late.since));
    }
    if (scene.redraw && mayDefer(now)) {
      // Move the picture painted with `painted`'s view to where the current view would put it.
      scene.redraw = false;
      const f = scene.view.k / painted!.k;
      renderer.shift(`translate(${scene.view.x - painted!.x * f}px, ${scene.view.y - painted!.y * f}px) scale(${f})`);
      movedAt = now;
      canvas.dataset.deferred = String(++deferred);
    } else if (movedAt && now - movedAt >= REST_MS) {
      // The view rests: paint it for real.
      scene.redraw = true;
    }
    if (scene.redraw) {
      scene.redraw = false;
      movedAt = 0;
      renderer.shift(null);
      const started = performance.now();
      const picture = hooks.picture(palette);
      // How many pictures were painted, and the cost of the frame's drawing (making the picture
      // and painting it, in milliseconds) with how many shapes, for the browser checks.
      canvas.dataset.pictures = String(renderer.paint(picture, hooks.images));
      paintMs = performance.now() - started;
      paintedAt = performance.now();
      painted = { x: scene.view.x, y: scene.view.y, k: scene.view.k, width: scene.width, height: scene.height };
      canvas.dataset.paintMs = paintMs.toFixed(1);
      canvas.dataset.shapes = String(picture.base.length + picture.top.length);
    }
    frameId = requestAnimationFrame(frame);
  };
  frameId = requestAnimationFrame(frame);

  return {
    stop() {
      observer.disconnect();
      themeObserver.disconnect();
      media.removeEventListener("change", repaint);
      cancelAnimationFrame(frameId);
      renderer.dispose();
    },
    setGpu(on) {
      if (on === gpu) return;
      gpu = on;
      renderer.dispose();
      renderer.shift(null);
      renderer = createRenderer(canvas, on, hooks.log);
      renderer.resize(scene.width, scene.height, dpr);
      scene.redraw = true;
    },
    renderer: () => renderer.kind(),
  };
}
